/**
 * IVR Agents API wrappers — admin-console 內的 IVR avatar 管理
 *
 * Backend endpoints:
 *   GET  /api/avatar                          — owner 自己的所有 avatars (含 ivr / survey)
 *   GET  /api/account/subAccounts?newMode=ivr — 子帳號 + 各自的 ivrAvatars 陣列
 *   GET  /api/qubbyAi/avatar/ivr-flow?id=     — single avatar 詳情含 ivrFlowConfig
 *   PUT  /api/avatar                          — 更新 avatar 欄位
 *
 *   GET  /ivr-builder/api/ivr/sessions?ownerUserId
 *   GET  /ivr-builder/api/ivr/sessions/:sid
 *   GET  /ivr-builder/api/ivr/sessions/:sid/messages
 *   POST /ivr-builder/api/ivr/sessions/:sid/messages  — SSE (用 useSSEChat 處理)
 */
import { api, get, post } from './client.js';
const patch = (p, body) => api('PATCH', p, { body });

/** 列 owner 自己的 IVR avatars（refresh=true 跳過 Redis 用最新 Firestore）*/
export async function fetchOwnerAvatars({ refresh = false } = {}) {
    const qs = refresh ? '&refresh=1' : '';
    const data = await get(`/api/avatar/mine?mode=ivr${qs}`);
    const list = data?.avatars || [];
    return list.map(normalizeAvatar);
}

/** 列子帳號名下所有 IVR avatars（扁平化）— backend route 實際路徑是 /api/account/subs */
export async function fetchSubAccountAvatars() {
    try {
        const r = await get('/api/account/subs?newMode=ivr');
        const subs = r?.data || [];
        const flat = [];
        for (const sub of subs) {
            if (sub.ivrAvatars && Array.isArray(sub.ivrAvatars)) {
                for (const a of sub.ivrAvatars) {
                    flat.push(normalizeAvatar({
                        ...a,
                        id: a.avatarId,
                        userId: sub.userId,
                        ownerEmail: sub.email,
                        ownerName: sub.name,
                        isSubAccount: true,
                    }));
                }
            }
        }
        return flat;
    } catch (e) {
        // 非母帳號會 403 — 視為沒子帳號
        if (e.status === 403) return [];
        throw e;
    }
}

/** 列 tenant 範圍內所有 IVR avatars（含子帳號擁有的 + 任何 tenant member 是 editor 的）
 *  跟後端 quota 用同一個 query → list 數字跟 quota pill 永遠對齊。
 *  排序：啟用的優先（停用的全部放最後），同 enabled 內按 updatedAt desc
 */
export async function fetchAllOwnedIvrAvatars({ refresh = false, hostId } = {}) {
    if (!hostId) {
        // fallback：沒 hostId → 走舊邏輯（owned + cubbies parentId sub），避免破壞 noTenant case
        const [own, sub] = await Promise.all([fetchOwnerAvatars({ refresh }), fetchSubAccountAvatars()]);
        const merged = [...own, ...sub];
        return merged.sort((a, b) => sortByEnabledUpdated(a, b));
    }
    // tenant scope — 跟 quota 一致
    const data = await get(`/api/ivr/admin/tenant-avatars?hostId=${encodeURIComponent(hostId)}`);
    const list = data?.avatars || [];
    return list.map(normalizeAvatar).sort(sortByEnabledUpdated);
}

function sortByEnabledUpdated(a, b) {
    const ea = a.enabled !== false ? 1 : 0;
    const eb = b.enabled !== false ? 1 : 0;
    if (ea !== eb) return eb - ea;
    const ta = toMillis(a.updatedAt) || toMillis(a.createdAt) || 0;
    const tb = toMillis(b.updatedAt) || toMillis(b.createdAt) || 0;
    return tb - ta;
}

/** 寫回 coverSeed（讓卡片封面固定）— PATCH /api/avatar/:avatarId/meta */
export async function patchAvatarCoverSeed(avatarId, coverSeed) {
    return patch(`/api/avatar/${encodeURIComponent(avatarId)}/meta`, { coverSeed });
}

/** 啟用 / 停用 avatar — 停用後 sip-relay 不可綁 DID */
export async function setAvatarEnabled(avatarId, enabled) {
    return patch(`/api/avatar/${encodeURIComponent(avatarId)}/meta`, { enabled });
}

/** 改 avatar 名稱 */
export async function renameAvatar(avatarId, name) {
    return patch(`/api/avatar/${encodeURIComponent(avatarId)}/meta`, { name });
}

/** 設品牌主色（webcall 分享頁前台 + embed 插件 accent）— '#rrggbb'，空字串 = 還原預設 terracotta。
 *  存進 avatar.brandColor → share-web /space 帶出 → 前台整頁套色。後端會清 avatar-/shared-space- cache。*/
export async function saveBrandColor(avatarId, brandColor) {
    return patch(`/api/avatar/${encodeURIComponent(avatarId)}/meta`, { brandColor });
}

/** 設語音球中央文字（webcall 分享頁）— 空字串 = 還原成「角色名前 2 字」。
 *  可用 `/` 指定斷行點（如「王家瑋/婦產科」）；沒指定前台會自動依球體寬度斷 1~3 行。*/
export async function saveOrbLabel(avatarId, orbLabel) {
    return patch(`/api/avatar/${encodeURIComponent(avatarId)}/meta`, { orbLabel });
}

/** 設語音球圖片（webcall 分享頁）— PNG dataURL；空字串 = 移除（回到原本的球）。
 *  後端上傳 S3/CDN → avatar.orbImageUrl，回傳 { orbImageUrl }。*/
export async function saveOrbImage(avatarId, orbImage) {
    return patch(`/api/avatar/${encodeURIComponent(avatarId)}/meta`, { orbImage });
}

/** 刪除 avatar — soft delete (isDeleted=true)，後端會清 Redis cache */
export async function deleteAvatar(avatarId) {
    return api('DELETE', `/api/avatar/${encodeURIComponent(avatarId)}`);
}

/** 確保 avatar 有 builder session（給 IVR chat 用）— 沒就建一個 */
export async function ensureBuilderSession(avatarId) {
    return post(`/api/avatar/${encodeURIComponent(avatarId)}/builder-session`, {});
}

/** 列 KB 名下所有 source 文件（refresh=true 跳過 Redis 用最新 Firestore 資料）
 *  avatarId：子帳號授權用（後端以 assignedAvatarIds 判定 KB 存取）*/
export async function fetchKbSources(kbId, { refresh = false, avatarId = null } = {}) {
    const params = [];
    if (refresh) params.push('refresh=1');
    if (avatarId) params.push(`avatarId=${encodeURIComponent(avatarId)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return get(`/api/knowledgeBase/${encodeURIComponent(kbId)}/sources${qs}`);
}

/** 切換 source 的 disabled 狀態 — disabled 後 vector search 跳過 */
export async function toggleKbSource(kbId, sourceId, disabled, avatarId = null) {
    return patch(
        `/api/knowledgeBase/${encodeURIComponent(kbId)}/sources/${encodeURIComponent(sourceId)}`,
        { disabled, ...(avatarId ? { avatarId } : {}) }
    );
}

/** 取單一 source 的所有 chunk 內文（給「點檔名 → 逐 chunk 預覽」用）*/
export async function fetchKbSourceChunks(kbId, sourceId, avatarId = null) {
    const qs = avatarId ? `?avatarId=${encodeURIComponent(avatarId)}` : '';
    return get(`/api/knowledgeBase/${encodeURIComponent(kbId)}/sources/${encodeURIComponent(sourceId)}/chunks${qs}`);
}

/** 取單一 avatar 完整 ivrFlowConfig（給 Flow viewer 用） */
export async function fetchAvatarFlowConfig(avatarId) {
    return get(`/api/qubbyAi/avatar/ivr-flow?id=${encodeURIComponent(avatarId)}`);
}

/** 取得開場白動態設計（日間 base MP3 + 夜間 + 界線 + 節慶排程）*/
export async function fetchGreetingConfig(avatarId) {
    const r = await fetchAvatarFlowConfig(avatarId);
    const cfg = r?.ivrFlowConfig || r?.data?.ivrFlowConfig || r || {};
    return {
        greetingMp3: cfg.greetingMp3 || cfg.twilioGreeting || null,   // 平日日間 = 預設開場白
        greetingNight: cfg.greetingNight || null,                     // 夜間（sameAsDay / 客製 mp3）
        greetingDayNight: cfg.greetingDayNight || null,               // 日夜界線
        greetingSchedule: Array.isArray(cfg.greetingSchedule) ? cfg.greetingSchedule : [],
        transferSchedule: cfg.transferSchedule || null,                 // 轉真人時段（非營業時段停止轉真人）
        tenantDefaultTransferDisabled: cfg.tenantDefaultTransferDisabled === true, // 客人沒指名時不退到租戶預設分機
    };
}

/** 儲存界線 + 夜間同上旗標 + 節慶排程（文字→MP3 另走 generateGreetingMp3）*/
export async function saveGreetingConfig(avatarId, { greetingDayNight, greetingNightSameAsDay, greetingNightText, greetingSchedule, transferSchedule, tenantDefaultTransferDisabled }) {
    return api('PUT', '/api/ivr-flow/greeting-schedule', {
        body: { id: avatarId, greetingDayNight, greetingNightSameAsDay, greetingNightText, greetingSchedule, transferSchedule, tenantDefaultTransferDisabled },
    });
}

/** 同步生成開場白 MP3（slot=day→greetingMp3 預設、night→greetingNight 客製）*/
export async function generateGreetingMp3(avatarId, text, slot = 'day') {
    return api('POST', '/api/ivr-flow/generate-greeting-mp3', { body: { avatarId, text, slot } });
}

// ─── helpers ─────────────────────────────────────────────────────

/**
 * 角色類型以「實際的 flow 長相」為準，不是以 avatar.flowMode 欄位為準。
 *
 * 為什麼：`avatar.flowMode` 是建立當下寫死的欄位，會跟真實 flow 脫節 ——
 * migrate/clone 出來的總機角色 create_avatar 一律寫 flowMode='full'，但 ivrFlowConfig 只有
 * router→transfer→end（實測：晶吉AI總機 clone 後在後台被標成「AI 客服」）。標錯不只是顯示問題：
 * 工作室會照 flowMode 決定顯示哪些動作、以及知識庫面板要不要提示「純總機不吃知識庫」。
 * 有 nodes 就直接看有沒有 knowledge 節點（= 通話時到底會不會查知識庫），這是唯一可信的依據。
 *
 * 🔴 後端 `ai_backend/ivr/lib/roleQuota.js` 的 avatarSeatType() 是這支的鏡像（AA/CS 配額計數用）。
 * 改這裡的規則就要同步改那裡，否則會出現「後台顯示沒滿卻建不了角色」（0813 東訊 tecom）。
 */
export function resolveFlowMode(a) {
    const nodes = a.ivrFlowConfig?.nodes;
    if (Array.isArray(nodes) && nodes.length > 0) {
        if (nodes.some((n) => n?.type === 'survey')) return 'survey';
        return nodes.some((n) => n?.type === 'knowledge') ? 'full' : 'transfer-only';
    }
    return a.flowMode || a.ivrFlowConfig?.flowMode || 'full';
}

function normalizeAvatar(a) {
    const id = a.id || a.avatarId;
    return {
        avatarId: id,
        name: a.name || '未命名角色',
        flowMode: resolveFlowMode(a),
        coverSeed: a.coverSeed || null,
        enabled: a.enabled === true,                    // 預設停用，明確 true 才算啟用（新建 avatar 必須手動啟用）
        spaceId: a.spaceId,
        openclawSessionId: a.openclawSessionId,
        userId: a.userId,
        ownerEmail: a.ownerEmail,
        ownerName: a.ownerName,
        isSubAccount: a.isSubAccount || false,
        // 共用租戶的商用子帳號：角色掛在哪個子帳號名下（tenant-avatars 回的 sharedAccountUid/Email，卡片標籤用）
        sharedAccountUid: a.sharedAccountUid || null,
        sharedAccountEmail: a.sharedAccountEmail || null,
        knowledgeBase: a.knowledgeBase || [],
        defaultVoiceId: a.defaultVoiceId,
        firstMessage: a.firstMessage,
        // 綁定狀態 — AvatarCard chips 用
        hostId: a.hostId || null,
        // ⛔ 0921 移除 `did`：那是沒人寫的孤兒欄位（綁定只寫 sipPhoneNumbers），
        //    畫面看得到的 91 隻已綁角色有 82 隻被它標成「未綁門號」、有值的 15 隻裡 7 隻是過期號碼。
        //    門號一律走 hooks/useTenantDids.js（讀進線路由，且支援一隻綁多支）。
        liveMode: a.liveMode === true,
        // type 旗標 — ivrMode=true 是 relay 平台基底,newMode='survey' 是問卷類型(IvrAgentWorkspacePage 用此 redirect 到 SurveyWorkspacePage)
        newMode: a.newMode || null,
        surveyMode: a.surveyMode === true,
        // survey workspace 用的欄位(SurveyWorkspacePage 編輯這些)
        scenario: a.scenario || '',
        surveyEndingScript: a.surveyEndingScript || '',
        languageCode: a.languageCode || a.defaultLanguage || 'zh-TW',
        transitionPhrases: a.transitionPhrases || [],
        voiceCorrectionWords: a.voiceCorrectionWords || [],
        ivrFlowConfig: a.ivrFlowConfig || null,   // 給 greetingMp3 用
        createdAt: a.createdAt,
        updatedAt: a.updatedAt,
        raw: a,
    };
}

/** 試聽音色 — backend 用 generate-greeting-standalone 邏輯生 MP3，回 audioUrl。
 *  provider='grok' → 後端走 Grok realtime TTS（否則 Gemini native）。 */
export async function previewVoice({ voiceId, text, language = 'zh-TW', provider = 'gemini' }) {
    return post('/api/ivr/admin/voice-preview', { voiceId, text, language, provider });
}

// ─── 即時互動 2.0 音色庫（複刻聲；id = 32 hex）───────────────────
/** 列出內建 + 自己的客製音色 → { voices:[{id,nameZh,nameEn,gender,tagZh,tagEn,sampleUrl,source}], limits:{minSec,maxSec} } */
export async function listCloneVoices() {
    return get('/api/ivr-flow/voices');
}
/** 上傳音檔做客製音色（後端截 30 秒 → 去背景音 → 建 model → 試聽）*/
export async function uploadCloneVoice({ file, nameZh, nameEn = '', gender = 'female', avatarId = null }) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('nameZh', nameZh);
    fd.append('nameEn', nameEn);
    fd.append('gender', gender);
    if (avatarId) fd.append('avatarId', avatarId);
    return api('POST', '/api/ivr-flow/voices/upload', { body: fd });
}
export async function deleteCloneVoice(id) {
    return api('DELETE', `/api/ivr-flow/voices/${encodeURIComponent(id)}`);
}

/** 清空對話記錄 — 只保留最早的 keep 則（預設 2 = 一開始建 avatar 的 user + assistant）*/
export async function clearSessionMessages(sessionId, { keep = 2 } = {}) {
    return api('DELETE', `/ivr-builder/api/ivr/sessions/${encodeURIComponent(sessionId)}/messages?keep=${keep}`);
}

function toMillis(t) {
    if (!t) return 0;
    if (typeof t === 'number') return t;
    if (typeof t === 'string') return new Date(t).getTime() || 0;
    if (t._seconds) return t._seconds * 1000;
    if (t.seconds) return t.seconds * 1000;
    if (t.toMillis) return t.toMillis();
    return 0;
}

// ─── 罐頭訊息（文字聊天開場問題 + AI 追問建議）───────────────────

/** 讀罐頭訊息設定（enabled 預設 false；messages 為開場問題清單）*/
export async function fetchCannedMessages(avatarId) {
    return get(`/api/ivr-flow/canned-messages?id=${encodeURIComponent(avatarId)}`);
}

/** 存罐頭訊息設定；開啟後 share-web 文字聊天顯示開場問題 + 每輪 AI 追問建議 */
export async function saveCannedMessages(avatarId, { enabled, messages }) {
    return api('PUT', '/api/ivr-flow/canned-messages', { body: { id: avatarId, enabled, messages } });
}

// ─── 通話行為（能不能插話 / 客戶不出聲時 / 開場白後沒出聲）───────────

/**
 * 讀通話行為。interruptMode 沒明確設時回 runtime 預設（客服=開場白唸完再收、總機=可隨時插話），
 * interruptExplicit=false 表示目前是吃預設。
 */
export async function fetchCallBehavior(avatarId) {
    return get(`/api/ivr-flow/call-behavior?id=${encodeURIComponent(avatarId)}`);
}

/** 存通話行為；三個插話旗標互斥、後端全部明確寫入，存完即清快取（下一通生效）*/
export async function saveCallBehavior(avatarId, { interruptMode, repromptSec, noResponseSec, noResponseAction, noResponseExt }) {
    return api('PUT', '/api/ivr-flow/call-behavior', {
        body: { id: avatarId, interruptMode, repromptSec, noResponseSec, noResponseAction, noResponseExt },
    });
}
